import { useCallback, useEffect, useRef, RefObject } from 'react';
import { Cell, MergedRegion, Selection, Slot } from './types';
import { isCellInRegion, cellsEqual, isCellInArea } from './utils';
import {
  Direction,
  areaAdjacentCells,
  areaForSelection,
  cellAdjacentCell,
  cellAdjacentCells,
  containingArea,
  regionToArea,
  shrinkingAreaForDirection,
} from './selectionUtils';
import { useDndContext } from '@dnd-kit/core';

const IGNORE_KEYS = ['CapsLock', 'Control', 'Alt', 'Meta', 'Shift'];

export function useSheetKeyboardAndMouseEffects<RawData>(
  mergedRegions: readonly MergedRegion[],
  columnCount: number,
  rowCount: number,
  selection: Selection | null,
  setSelection: React.Dispatch<React.SetStateAction<Selection | null>>,
  getSlotForCell: (cell: Cell) => Slot<RawData> | undefined,
  contextMenuOpen: boolean,
  disableKeyboardAndMouse: boolean,
  onUndo: () => void,
  onRedo: () => void,
  onBulkClear: () => void,
  onCopy: () => void,
  onPaste: () => void,
  sheetRef: RefObject<HTMLDivElement>,
): {
  onMouseMove: (event: React.MouseEvent) => void;
  onMouseOver: (event: React.MouseEvent, cell: Cell) => void;
  onMouseDown: (event: React.MouseEvent, cell: Cell) => void;
} {
  const autoScrollIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    const keyDownFn = (e: KeyboardEvent) => {
      if (contextMenuOpen || disableKeyboardAndMouse) {
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        // TODO: Reset input? Where would the initial input be stored? Is that just up to each slot?
        if (selection?.editing) {
          setSelection({
            base: selection.base,
            area: null,
            isHighlighting: false,
            editing: false,
          });
        }
      } else if (e.key === 'Enter') {
        if (!selection) {
          return;
        }

        const slot = getSlotForCell(selection.base);
        const editBehavior = slot?.getEditBehavior(selection.base);

        if (!selection.editing && editBehavior?.type === 'input') {
          editBehavior.onStart();
          setSelection({
            ...selection,
            area: null,
            isHighlighting: false,
            editing: true,
          });
          return;
        }

        const base = cellAdjacentCell(
          selection.base,
          mergedRegions,
          'down',
          columnCount,
          rowCount,
        );

        if (selection.editing && editBehavior?.type === 'input') {
          editBehavior.onCommit();
        } else if (!selection.editing && editBehavior?.type === 'trigger') {
          editBehavior.onEnter();
        }

        setSelection({
          base,
          area: null,
          isHighlighting: false,
          editing: false,
        });
      } else if (e.key === 'ArrowDown') {
        // Disabled scroll with arrow keys
        if (!selection?.editing) {
          e.preventDefault();
        }
        setSelection(
          arrowKeySelectionUpdate(
            selection,
            mergedRegions,
            'down',
            e.shiftKey,
            rowCount,
            columnCount,
          ),
        );
      } else if (e.key === 'ArrowUp') {
        // Disabled scroll with arrow keys
        if (!selection?.editing) {
          e.preventDefault();
        }
        setSelection(
          arrowKeySelectionUpdate(
            selection,
            mergedRegions,
            'up',
            e.shiftKey,
            rowCount,
            columnCount,
          ),
        );
      } else if (e.key === 'ArrowLeft') {
        // Disabled scroll with arrow keys
        if (!selection?.editing) {
          e.preventDefault();
        }
        setSelection(
          arrowKeySelectionUpdate(
            selection,
            mergedRegions,
            'left',
            e.shiftKey,
            rowCount,
            columnCount,
          ),
        );
      } else if (e.key === 'ArrowRight') {
        // Disabled scroll with arrow keys
        if (!selection?.editing) {
          e.preventDefault();
        }
        setSelection(
          arrowKeySelectionUpdate(
            selection,
            mergedRegions,
            'right',
            e.shiftKey,
            rowCount,
            columnCount,
          ),
        );
      } else if (e.key === 'Tab') {
        // Tab moves right even if editing
        if (!selection) {
          return;
        }
        e.preventDefault();

        const base = cellAdjacentCell(
          selection.base,
          mergedRegions,
          e.shiftKey ? 'left' : 'right',
          columnCount,
          rowCount,
        );

        const slot = getSlotForCell(selection.base);
        const editBehavior = slot?.getEditBehavior(selection.base);

        if (selection.editing && editBehavior?.type === 'input') {
          editBehavior.onCommit();
        }

        setSelection({
          base,
          area: null,
          isHighlighting: false,
          editing: false,
        });
      } else if (e.key === 'Backspace') {
        if (!selection || selection.editing) {
          return;
        }

        e.preventDefault();
        onBulkClear();
      } else if (IGNORE_KEYS.includes(e.key)) {
        return;
      } else if (e.key?.length === 1) {
        if (selection?.editing) {
          return;
        }
        if (e.metaKey || e.ctrlKey) {
          if (e.key === 'c') {
            e.preventDefault();
            onCopy();
          } else if (e.key === 'v') {
            // If actually using the Clipboard API at some point, we'll want to use an onPaste event handler
            // On the base sheet or the selected input in order to ensure clipboard data is available
            // However, we are not using the Clipboard API at the moment
            onPaste();
            return;
          } else if (e.key === 'z') {
            e.preventDefault();
            if (e.shiftKey) {
              onRedo();
            } else {
              onUndo();
            }
          }
          // TODO: Add cut via "x"
          return;
        } else {
          if (!selection) {
            return;
          }
          e.preventDefault();

          const slot = getSlotForCell(selection.base);
          const editBehavior = slot?.getEditBehavior(selection.base);

          if (!selection.editing && editBehavior?.type === 'input') {
            editBehavior.onStart(e.key);
            setSelection({
              ...selection,
              area: null,
              isHighlighting: false,
              editing: true,
            });
          }

          // Check that length equals 1 to avoid things like "CapsLock" in case I missed anything
          // TODO: Start editing with the new value added
        }
      }
    };

    document.addEventListener('keydown', keyDownFn);

    return () => {
      document.removeEventListener('keydown', keyDownFn);
    };
  }, [
    rowCount,
    columnCount,
    selection,
    contextMenuOpen,
    onCopy,
    onPaste,
    getSlotForCell,
    mergedRegions,
    disableKeyboardAndMouse,
    setSelection,
    onBulkClear,
    onRedo,
    onUndo,
  ]);

  const dndContext = useDndContext();
  const isDragging = dndContext.active !== null;

  // SC-5486: highlight-drag selection updates used to re-render the entire
  // grid per mouseover. Coalesce them: at most one selection update per
  // animation frame, computed from the latest hovered cell, and skipped
  // entirely (no dispatch) when the containing area did not change.
  const pendingHoverCellRef = useRef<Cell | null>(null);
  const hoverFrameRef = useRef<number | null>(null);
  const selectionRef = useRef(selection);

  useEffect(() => {
    selectionRef.current = selection;
  }, [selection]);

  const flushPendingHover = useCallback(() => {
    hoverFrameRef.current = null;
    const cell = pendingHoverCellRef.current;
    pendingHoverCellRef.current = null;
    const prev = selectionRef.current;
    if (!cell || !prev?.isHighlighting) {
      return;
    }
    const nextArea = containingArea(
      { from: prev.base, to: cell },
      [],
      mergedRegions,
    );
    if (
      prev.area &&
      cellsEqual(prev.area.from, nextArea.from) &&
      cellsEqual(prev.area.to, nextArea.to)
    ) {
      return;
    }
    // Spread the freshest committed state, not the ref snapshot: the ref
    // syncs in a passive effect, and a frame firing between commit and
    // effect would otherwise apply a stale area onto a new selection.
    // Re-check against the state the updater actually sees — same base,
    // still highlighting, not editing — before applying.
    setSelection((current) => {
      if (
        !current ||
        current.editing ||
        !current.isHighlighting ||
        !cellsEqual(current.base, prev.base)
      ) {
        return current;
      }
      if (
        current.area &&
        cellsEqual(current.area.from, nextArea.from) &&
        cellsEqual(current.area.to, nextArea.to)
      ) {
        return current;
      }
      return { ...current, area: nextArea };
    });
  }, [mergedRegions, setSelection]);

  const onMouseUp = useCallback(() => {
    // Flush any coalesced hover update before clearing highlighting: a sweep
    // that ends within one frame must still include the release cell.
    if (hoverFrameRef.current !== null) {
      cancelAnimationFrame(hoverFrameRef.current);
      hoverFrameRef.current = null;
    }
    flushPendingHover();

    setSelection(
      (selection) =>
        selection && {
          ...selection,
          isHighlighting: false,
        },
    );
    document.removeEventListener('mouseup', onMouseUp);

    // Clear auto-scroll interval when mouse is released
    if (autoScrollIntervalRef.current !== null) {
      window.clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, [setSelection, flushPendingHover]);

  useEffect(() => {
    return () => {
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
        hoverFrameRef.current = null;
      }
    };
  }, []);

  const onMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (
        selection &&
        !isDragging &&
        !selection.isHighlighting &&
        !selection.editing &&
        event.buttons === 1
      ) {
        setSelection({
          ...selection,
          isHighlighting: true,
        });
        document.addEventListener('mouseup', onMouseUp);
      }

      // Auto-scrolling when highlighting
      if (selection?.isHighlighting && sheetRef.current) {
        // Clear any existing interval first
        if (autoScrollIntervalRef.current !== null) {
          window.clearInterval(autoScrollIntervalRef.current);
          autoScrollIntervalRef.current = null;
        }

        const sheet = sheetRef.current;
        const sheetRect = sheet.getBoundingClientRect();
        const { clientX, clientY } = event;

        const SCROLL_THRESHOLD = 50; // pixels from edge to trigger scrolling
        const SCROLL_SPEED = 10; // pixels to scroll per interval

        const distanceFromTop = clientY - sheetRect.top;
        const distanceFromBottom = sheetRect.bottom - clientY;
        const distanceFromLeft = clientX - sheetRect.left;
        const distanceFromRight = sheetRect.right - clientX;

        let scrollX = 0;
        let scrollY = 0;

        if (distanceFromTop < SCROLL_THRESHOLD) {
          // Near top edge - scroll up
          scrollY = -SCROLL_SPEED;
        } else if (distanceFromBottom < SCROLL_THRESHOLD) {
          // Near bottom edge - scroll down
          scrollY = SCROLL_SPEED;
        }

        if (distanceFromLeft < SCROLL_THRESHOLD) {
          // Near left edge - scroll left
          scrollX = -SCROLL_SPEED;
        } else if (distanceFromRight < SCROLL_THRESHOLD) {
          // Near right edge - scroll right
          scrollX = SCROLL_SPEED;
        }

        // Start auto-scrolling if needed
        if (scrollX !== 0 || scrollY !== 0) {
          autoScrollIntervalRef.current = window.setInterval(() => {
            sheet.scrollBy(scrollX, scrollY);
          }, 50);
        }
      }
    },
    [selection, isDragging, sheetRef, setSelection, onMouseUp],
  );

  // Clean up auto-scroll interval on unmount
  useEffect(() => {
    return () => {
      if (autoScrollIntervalRef.current !== null) {
        window.clearInterval(autoScrollIntervalRef.current);
      }
    };
  }, []);

  const onMouseOver = useCallback(
    (e: React.MouseEvent, cell: Cell) => {
      if (!selection?.isHighlighting) {
        return;
      }

      pendingHoverCellRef.current = cell;
      if (hoverFrameRef.current === null) {
        hoverFrameRef.current = requestAnimationFrame(flushPendingHover);
      }
    },
    [selection, flushPendingHover],
  );

  const onMouseDown = useCallback(
    (e: React.MouseEvent, cell: Cell) => {
      const shiftHeld = e.shiftKey;
      const isRightClick = e.button === 2;

      if (selection?.isHighlighting) {
        console.error("Selection shouldn't be highlighting onMouseDown");
      }

      // A mousedown starts a new selection; a hover update coalesced for the
      // next frame belongs to the old one — drop it.
      if (hoverFrameRef.current !== null) {
        cancelAnimationFrame(hoverFrameRef.current);
        hoverFrameRef.current = null;
        pendingHoverCellRef.current = null;
      }

      if (!selection) {
        setSelection({
          base: cell,
          area: null,
          isHighlighting: false,
          editing: false,
        });
        return;
      }

      // This allows moving the cursor within the currently editing cell
      if (selection.editing && cellsEqual(cell, selection.base)) {
        return;
      }

      // Don't change selection if right clicking in highlighted area
      if (
        selection.area &&
        isRightClick &&
        isCellInArea(cell, selection.area)
      ) {
        return;
      }

      if (shiftHeld) {
        setSelection({
          ...selection,
          editing: false,
          area: containingArea(
            {
              from: selection.base,
              to: cell,
            },
            [],
            mergedRegions,
          ),
        });
      } else {
        setSelection({
          base: cell,
          area: null,
          isHighlighting: false,
          editing: false,
        });
      }

      onCommitIfEditing(selection, getSlotForCell);
    },
    [selection, getSlotForCell, setSelection, mergedRegions],
  );

  return {
    onMouseMove,
    onMouseOver,
    onMouseDown,
  };
}

export function arrowKeySelectionUpdate(
  selection: Selection | null,
  mergedRegions: readonly MergedRegion[],
  direction: Direction,
  shiftHeld: boolean,
  rowCount: number,
  columnCount: number,
): Selection | null {
  if (!selection || selection.editing) {
    return selection;
  }

  if (shiftHeld) {
    let newCells: Cell[];
    if (selection.area) {
      const shrunkArea = shrinkingAreaForDirection(
        selection.base,
        selection.area,
        direction,
        mergedRegions,
      );
      if (shrunkArea) {
        // Edge case: If shrinking area would result in selection of only base cell, then remove area
        const selectionBaseRegion = mergedRegions.find((region) => {
          return isCellInRegion(selection.base, region);
        });
        const selectionBaseArea = selectionBaseRegion
          ? regionToArea(selectionBaseRegion)
          : null;

        const shrunkToBaseCell =
          cellsEqual(shrunkArea.from, selection.base) &&
          cellsEqual(shrunkArea.to, selection.base);
        const shrunkToBaseArea =
          selectionBaseArea &&
          isCellInArea(shrunkArea.from, selectionBaseArea) &&
          isCellInArea(shrunkArea.to, selectionBaseArea);

        if (shrunkToBaseCell || shrunkToBaseArea) {
          return {
            base: selection.base,
            area: null,
            isHighlighting: false,
            editing: false,
          };
        }

        return {
          base: selection.base,
          area: shrunkArea,
          isHighlighting: false,
          editing: false,
        };
      }

      newCells = areaAdjacentCells(
        selection.area,
        direction,
        columnCount,
        rowCount,
      );
    } else {
      newCells = cellAdjacentCells(
        selection.base,
        mergedRegions,
        direction,
        columnCount,
        rowCount,
      );
    }

    return {
      base: selection.base,
      area: containingArea(
        areaForSelection(selection, mergedRegions),
        newCells,
        mergedRegions,
      ),
      isHighlighting: false,
      editing: false,
    };
  } else {
    const toCell = cellAdjacentCell(
      selection.base,
      mergedRegions,
      direction,
      columnCount,
      rowCount,
    );

    return {
      base: toCell,
      area: null,
      isHighlighting: false,
      editing: false,
    };
  }
}

export function onCommitIfEditing<RawData>(
  selection: Selection | null,
  getSlotForCell: (cell: Cell) => Slot<RawData> | undefined,
) {
  if (selection?.editing) {
    const slot = getSlotForCell(selection.base);
    const editBehavior = slot?.getEditBehavior(selection.base);

    if (editBehavior?.type === 'input') {
      editBehavior.onCommit();
    }
  }
}
